
import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { ShoppingCart, Ingredient, Language, ShoppingCategory } from '../types';
import TrashIcon from './icons/TrashIcon';
import PlusIcon from './icons/PlusIcon';
import DragHandleIcon from './icons/DragHandleIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import MeatIcon from './icons/MeatIcon';
import XIcon from './icons/XIcon';
import SmartUnitCapsule from './SmartUnitCapsule';
import { getTranslation, isRTL } from '../utils/translations';
import { getSectionDisplayName, toCanonicalSectionKey } from '../utils/sectionNames';
import { SHOPPING_CATEGORIES } from '../utils/shoppingCategories';

interface AddShoppingCartModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddCart: (cart: Omit<ShoppingCart, 'id'>) => void;
    onUpdateCart: (cart: ShoppingCart) => void;
    cartToEdit?: ShoppingCart;
    language?: Language;
}

interface IngredientWithId extends Ingredient {
    _id: string;
}

const CheckIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

const STANDARD_UNITS = ['kg', 'g', 'unit', 'ml', 'liter', 'tsp', 'tbsp', 'cup', 'container', 'tub', 'can', 'bottle', 'bag', 'box', 'roll', 'to taste', 'pinch', 'bunch', 'package', 'clove', 'head', 'stalk', 'shot'];

const AddShoppingCartModal: React.FC<AddShoppingCartModalProps> = ({ isOpen, onClose, onAddCart, onUpdateCart, cartToEdit, language }) => {
    const isEditMode = !!cartToEdit;
    const t = (key: any) => getTranslation(language, key);
    const rtl = isRTL(language);

    const [name, setName] = useState('');
    const [category, setCategory] = useState<ShoppingCategory>('General');
    const [ingredients, setIngredients] = useState<IngredientWithId[]>([]);
    const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
    const [editingSection, setEditingSection] = useState<{ index: number, name: string } | null>(null);
    const [focusIngId, setFocusIngId] = useState<string | null>(null);
    
    const editInputRef = useRef<HTMLInputElement>(null);
    const ingredientInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    useEffect(() => {
        if (isOpen) {
            if (isEditMode && cartToEdit) {
                setName(cartToEdit.name);
                setCategory(cartToEdit.category || 'General');
                const ings = cartToEdit.ingredients.map(i => ({
                    ...i,
                    section: toCanonicalSectionKey(i.section, t),
                    _id: Math.random().toString(36).substr(2, 9)
                }));
                setIngredients(ings);
                setCollapsedSections(new Set());
            } else {
                setName('');
                setCategory('General');
                const initialSection = 'general';
                setIngredients([{ 
                    name: '', quantity: 1, unit: 'unit', isMeat: false, includedInShoppingList: true, section: initialSection,
                    _id: Math.random().toString(36).substr(2, 9) 
                }]);
                setCollapsedSections(new Set());
            }
        }
    }, [isEditMode, cartToEdit, isOpen, language]);

    useEffect(() => {
        if (editingSection && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingSection?.index]);

    useEffect(() => {
        if (focusIngId && ingredientInputRefs.current[focusIngId]) {
            ingredientInputRefs.current[focusIngId]?.focus();
            setFocusIngId(null);
        }
    }, [focusIngId]);

    const groupedIngredients = useMemo(() => {
        const groups: Record<string, IngredientWithId[]> = {};
        ingredients.forEach((ing) => {
            const section = toCanonicalSectionKey(ing.section, t);
            if (!groups[section]) groups[section] = [];
            groups[section].push(ing);
        });
        return Object.entries(groups).map(([name, items]) => [name, items] as [string, IngredientWithId[]]);
    }, [ingredients, t]);

    // Calculate duplicates for visual feedback
    const duplicateSet = useMemo(() => {
        const counts = new Map<string, number>();
        const dups = new Set<string>();
        ingredients.forEach(i => {
            const n = i.name.trim().toLowerCase();
            if (n) {
                counts.set(n, (counts.get(n) || 0) + 1);
            }
        });
        counts.forEach((count, name) => {
            if (count > 1) dups.add(name);
        });
        return dups;
    }, [ingredients]);

    const handleAddIngredient = (section?: string) => {
        const targetSection = section || (ingredients.length > 0 ? toCanonicalSectionKey(ingredients[ingredients.length - 1].section, t) : 'general');
        
        // Prevent adding new item if last item in this section is empty
        const sectionItems = ingredients.filter(i => toCanonicalSectionKey(i.section, t) === targetSection);
        if (sectionItems.length > 0) {
            const lastItem = sectionItems[sectionItems.length - 1];
            if (!lastItem.name.trim()) {
                setFocusIngId(lastItem._id);
                return;
            }
        }

        const newId = Math.random().toString(36).substr(2, 9);
        setIngredients([...ingredients, { 
            name: '', quantity: 1, unit: 'unit', isMeat: false, includedInShoppingList: true, section: targetSection,
            _id: newId 
        }]);
        setFocusIngId(newId);
    };

    const handleRenameSection = (oldName: string, newName: string) => {
        setIngredients(ingredients.map(i => i.section === oldName ? { ...i, section: newName } : i));
    };

    const handleConfirmRename = (oldName: string) => {
        if (editingSection && editingSection.name.trim()) {
            handleRenameSection(oldName, editingSection.name.trim());
        }
        setEditingSection(null);
    };

    const handleCancelRename = () => {
        setEditingSection(null);
    };

    const toggleSection = (name: string) => {
        const next = new Set(collapsedSections);
        if (next.has(name)) next.delete(name); else next.add(name);
        setCollapsedSections(next);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Auto-remove duplicates on save (keeping first instance)
        // Also remove empty items
        const seenNames = new Set<string>();
        const uniqueIngredients: Ingredient[] = [];
        
        for (const ing of ingredients) {
            const cleanName = ing.name.trim();
            if (!cleanName) continue; // Skip empty
            
            const normalized = cleanName.toLowerCase();
            if (seenNames.has(normalized)) {
                continue; // Skip duplicate
            }
            seenNames.add(normalized);
            
            // Push clean version (without _id)
            const { _id, ...rest } = ing;
            uniqueIngredients.push(rest);
        }

        if (isEditMode && cartToEdit) {
            onUpdateCart({ ...cartToEdit, name, category, ingredients: uniqueIngredients });
        } else {
            onAddCart({ name, ingredients: uniqueIngredients, category, ownerId: '', householdId: '' });
        }
        onClose();
    };

    if (!isOpen) return null;

    const inputBaseClass = "w-full p-2 h-12 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-sky-500 outline-none transition-all font-bold";

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4 animate-fade-in font-rubik">
            <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-white/20 dark:border-slate-800 transition-colors">
                <div className="p-6 border-b flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 rounded-xl">
                            <PlusIcon className="w-6 h-6" />
                        </div>
                        <h2 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{isEditMode ? t('editCart') : t('addCart')}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-300 dark:text-slate-600 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
                        <XIcon className="w-8 h-8" />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="flex flex-col flex-grow overflow-hidden">
                    <div className="p-6 overflow-y-auto flex-grow space-y-8 scrollbar-none bg-slate-50/30 dark:bg-slate-900/30">
                        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">{t('nameLabel')}</label>
                                    <input 
                                        type="text" required value={name} 
                                        onChange={e => setName(e.target.value)} 
                                        className={inputBaseClass} 
                                        placeholder={t('cartLabel')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">{t('category')}</label>
                                    <select 
                                        value={category} 
                                        onChange={e => setCategory(e.target.value as ShoppingCategory)} 
                                        className={inputBaseClass}
                                    >
                                        {SHOPPING_CATEGORIES.map(c => (
                                            <option key={c} value={c} className="dark:bg-slate-800">{t(c.toLowerCase() as any)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {groupedIngredients.map(([sectionName, items], sectionIdx) => {
                                const isCollapsed = collapsedSections.has(sectionName);
                                const isEditingThis = editingSection?.index === sectionIdx;

                                return (
                                    <div key={sectionIdx} className="bg-white dark:bg-slate-900 rounded-[2rem] border border-stone-100 dark:border-slate-800 overflow-hidden shadow-sm transition-all duration-300">
                                        <div className="flex justify-between items-center p-5 bg-slate-50/50 dark:bg-slate-800/50 cursor-pointer group" onClick={() => toggleSection(sectionName)}>
                                            <div className="flex items-center gap-3 flex-grow" onClick={e => e.stopPropagation()}>
                                                <div className={`text-slate-300 dark:text-slate-600 transition-transform duration-300 group-hover:text-slate-500 dark:group-hover:text-slate-400 ${isCollapsed ? 'ltr:-rotate-90 rtl:rotate-90' : ''}`} onClick={() => toggleSection(sectionName)}>
                                                    <ChevronDownIcon className="w-5 h-5" />
                                                </div>
                                                
                                                {isEditingThis ? (
                                                    <div className="flex items-center gap-2 flex-grow animate-fade-in">
                                                        <input 
                                                            ref={editInputRef}
                                                            type="text" 
                                                            value={editingSection.name} 
                                                            onChange={(e) => setEditingSection({ ...editingSection, name: e.target.value })}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleConfirmRename(sectionName);
                                                                if (e.key === 'Escape') handleCancelRename();
                                                            }}
                                                            className="bg-white dark:bg-slate-800 border border-sky-400 rounded-lg px-2 py-1 text-base font-black text-sky-600 dark:text-sky-400 uppercase tracking-tight w-full focus:ring-2 ring-sky-100 outline-none" 
                                                            placeholder={t('sectionNamePlaceholder')} 
                                                        />
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleConfirmRename(sectionName)}
                                                            className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors"
                                                        >
                                                            <CheckIcon className="w-5 h-5" />
                                                        </button>
                                                        <button 
                                                            type="button"
                                                            onClick={handleCancelRename}
                                                            className="p-1 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors"
                                                        >
                                                            <XIcon className="w-5 h-5" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button 
                                                        type="button"
                                                        onClick={() => setEditingSection({ index: sectionIdx, name: getSectionDisplayName(sectionName, t) })}
                                                        className="bg-transparent border-none focus:ring-0 p-0 text-base font-black text-sky-600 dark:text-sky-400 uppercase tracking-tight text-start flex-grow hover:opacity-80 transition-opacity" 
                                                    >
                                                        {getSectionDisplayName(sectionName, t)}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button type="button" onClick={(e) => { e.stopPropagation(); handleAddIngredient(sectionName); if (isCollapsed) toggleSection(sectionName); }} className="p-1.5 bg-white dark:bg-slate-800 text-brand-sage dark:text-brand-sage rounded-lg shadow-sm border border-stone-100 dark:border-slate-700 transition-all active:scale-95"><PlusIcon className="w-4 h-4" /></button>
                                                <button type="button" onClick={(e) => { e.stopPropagation(); if (window.confirm(t('delete') + '?')) setIngredients(ingredients.filter(i => i.section !== sectionName)); }} className="p-1.5 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"><TrashIcon className="w-4 h-4" /></button>
                                            </div>
                                        </div>

                                        {!isCollapsed && (
                                            <div className="p-2 space-y-2 bg-white dark:bg-slate-900">
                                                {items.map((ing) => {
                                                    const realIdx = ingredients.findIndex(i => i._id === ing._id);
                                                    const isDuplicate = duplicateSet.has(ing.name.trim().toLowerCase());
                                                    
                                                    return (
                                                        <div key={ing._id} className={`flex items-center gap-2 p-2 pr-3 rounded-xl border transition-all group shadow-sm ${isDuplicate ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-800' : 'bg-slate-50/50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                                                            <div className="text-slate-200 dark:text-slate-700 cursor-grab active:cursor-grabbing p-1 flex-shrink-0 hover:text-slate-400 dark:hover:text-slate-500">
                                                                <DragHandleIcon className="w-4 h-4" />
                                                            </div>
                                                            
                                                            {/* Item Name */}
                                                            <div className="flex-grow min-w-0 relative">
                                                                <input 
                                                                    ref={el => { if(el) ingredientInputRefs.current[ing._id] = el; }}
                                                                    type="text" 
                                                                    value={ing.name} 
                                                                    onChange={e => {
                                                                        const next = [...ingredients];
                                                                        next[realIdx].name = e.target.value;
                                                                        setIngredients(next);
                                                                    }} 
                                                                    onKeyDown={e => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            handleAddIngredient(sectionName);
                                                                        }
                                                                    }}
                                                                    className={`w-full bg-transparent border-none focus:ring-0 p-0 font-bold text-sm sm:text-base placeholder:font-normal ${isDuplicate ? 'text-red-700 dark:text-red-300 placeholder:text-red-300' : 'text-slate-700 dark:text-slate-200 placeholder:text-slate-300 dark:placeholder:text-slate-600'}`} 
                                                                    placeholder={t('ingredientNamePlaceholder')} 
                                                                />
                                                                {isDuplicate && (
                                                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 text-[9px] font-bold text-red-500 dark:text-red-400 bg-white dark:bg-slate-900 px-1.5 py-0.5 rounded border border-red-100 dark:border-red-900 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                                                                        {t('duplicate')}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Actions & Quantity */}
                                                            <div className="flex items-center gap-2">
                                                                {/* Quantity Capsule */}
                                                                <SmartUnitCapsule 
                                                                    value={ing.quantity}
                                                                    unit={ing.unit}
                                                                    onChange={(val, unit) => {
                                                                        const next = [...ingredients];
                                                                        next[realIdx].quantity = val;
                                                                        next[realIdx].unit = unit;
                                                                        setIngredients(next);
                                                                    }}
                                                                    language={language}
                                                                    isMeat={ing.isMeat}
                                                                    availableUnits={STANDARD_UNITS}
                                                                />

                                                                <button 
                                                                    type="button" 
                                                                    onMouseDown={(e) => { e.preventDefault(); setIngredients(ingredients.filter(i => i._id !== ing._id)); }}
                                                                    className="p-1.5 text-slate-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors flex-shrink-0"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <button type="button" onClick={() => handleAddIngredient(sectionName)} className="w-full py-3 mt-1 border-2 border-dashed border-stone-100 dark:border-slate-700 text-brand-sage dark:text-brand-sage font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-brand-sage/20 rounded-2xl flex items-center justify-center gap-2 transition-all"><PlusIcon className="w-4 h-4" /> {t('add')} {t('to')} {getSectionDisplayName(sectionName, t)}</button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            
                            <button 
                                type="button" 
                                onClick={() => {
                                    const nextName = `${t('section')} ${groupedIngredients.length + 1}`;
                                    handleAddIngredient(nextName);
                                }} 
                                className="w-full py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 text-sky-600 dark:text-sky-400 font-black text-sm uppercase tracking-widest hover:bg-sky-50 dark:hover:bg-sky-900/20 hover:border-sky-300 dark:hover:border-sky-700 rounded-[2.5rem] flex items-center justify-center gap-2 transition-all"
                            >
                                <PlusIcon className="w-6 h-6" /> {t('addNewSection')}
                            </button>
                        </div>
                    </div>

                    <div className="p-6 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center gap-4 shrink-0 shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.05)]">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            className="px-8 py-4 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-black rounded-2xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs"
                        >
                            {t('cancel')}
                        </button>
                        <button 
                            type="submit" 
                            className="px-14 py-4 bg-slate-900 dark:bg-slate-700 text-white font-black rounded-2xl hover:bg-slate-800 dark:hover:bg-slate-600 shadow-xl shadow-slate-200 dark:shadow-slate-900/50 transition-all uppercase tracking-widest text-sm"
                        >
                            {t('save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddShoppingCartModal;
